import React, { useState } from "react";
import styled from "styled-components";
import { Card, Col, Form, Row } from "react-bootstrap";
import ValidationError, {
  ValidationErrorResponse,
} from "../../../services/ValidationError";
import SuccessButton from "../../organisms/SuccessButton";
import ServerValidationErrorAlert from "../../organisms/ServerValidationErrorAlert";
import ServerErrorModal from "../../organisms/ServerErrorModal";
import ServerErrorAlert from "../../organisms/ServerErrorAlert";
import LoadingScreen from "../../organisms/LoadingScreen";
import { StyledFormControl } from "../../organisms/StyledFormControl";
import CardTitle from "../../organisms/CardTitle";
import Exito, { ExitoOption } from "../../organisms/Exito";
import {
  crearProducto,
  NuevoProducto,
  useListarUnidades,
} from "../../../services/productosServicios";
import { Controller, useForm } from "react-hook-form";
import CurrencyFormat from "react-currency-format";
import { useMutation } from "@tanstack/react-query";
import { parseCurrency } from "../../../utils/numberUtils";

interface Model {
  nombre: string;
  descripcion?: string;
  unidad?: string;
  precioUnitario: string;
}

const defaultValues: Model = {
  nombre: "",
  descripcion: "",
  unidad: "",
  precioUnitario: "",
};

function FormSection() {
  const [validatedForm, setValidatedForm] = useState(false);
  const [serverFailedValidations, setServerFailedValidations] = useState(
    {} as ValidationErrorResponse
  );
  const [serverErrorOcurred, setServerErrorOcurred] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<Model>({
    defaultValues,
    mode: "all",
  });
  const { isLoading: unidadesIsLoading, data: unidades } = useListarUnidades();
  const creacion = useMutation(crearProducto, {
    onSuccess: () => {
      setValidatedForm(true);
      setIsFinished(true);
    },
    onError: (error) => {
      if (error instanceof ValidationError) {
        setServerFailedValidations(error.getFailedValidations());
      } else {
        setServerErrorOcurred(true);
      }
    },
  });

  const onSubmit = (model: Model) => {
    setServerFailedValidations({} as ValidationErrorResponse);
    setServerErrorOcurred(false);

    const nuevoProducto: NuevoProducto = {
      nombre: model.nombre,
      descripcion: model.descripcion,
      idUnidad: model.unidad!,
      precioUnitario: parseCurrency(model.precioUnitario, "$", ","),
    };

    creacion.mutate(nuevoProducto);
  };

  if (isFinished) {
    return <Exito title={"¡Creación exitosa!"} options={exitoOptions} />;
  }

  return (
    <Row className="justify-content-center">
      <div className="col-xs-12 col-md-10 col-lg-8 col-xxl-5">
        <LoadingScreen isLoading={unidadesIsLoading} />
        <Card>
          <StyledCardHeader className="pt-4 pb-4">
            <CardTitle>Crear un Producto</CardTitle>
          </StyledCardHeader>
          <Card.Body>
            {serverFailedValidations &&
              Object.keys(serverFailedValidations).length > 0 && (
                <Row className="justify-content-center mb-3">
                  <ServerValidationErrorAlert
                    serverFailedValidations={serverFailedValidations}
                    wrapperClass="col col-9"
                  />
                </Row>
              )}

            {serverErrorOcurred && (
              <Row className="justify-content-center mb-3">
                <ServerErrorAlert class="col col-9" />
              </Row>
            )}

            {serverErrorOcurred && <ServerErrorModal />}

            <Row className="justify-content-center">
              <Form
                className="col col-9"
                noValidate
                validated={validatedForm}
                onSubmit={handleSubmit(onSubmit)}
              >
                <Form.Group as={Col} controlId="formNombre" className="mb-3">
                  <Form.Label>Nombre</Form.Label>
                  <Controller
                    control={control}
                    name="nombre"
                    defaultValue=""
                    rules={{ required: true, minLength: 1 }}
                    render={({
                      field: { onChange, onBlur, value, ref },
                      fieldState: { invalid, isTouched, isDirty },
                    }) => (
                      <StyledFormControl
                        onChange={onChange}
                        value={value}
                        ref={ref}
                        onBlur={onBlur}
                        required
                        type="text"
                        isInvalid={isTouched && invalid}
                        isValid={isDirty && isTouched && !invalid}
                      />
                    )}
                  />
                  {errors.nombre && (
                    <Form.Control.Feedback type="invalid">
                      {errors.nombre.type === "required" && (
                        <span>Requerido</span>
                      )}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group
                  as={Col}
                  controlId="formDescripcion"
                  className="mb-3"
                >
                  <Form.Label>Descripción</Form.Label>
                  <Controller
                    control={control}
                    name="descripcion"
                    defaultValue=""
                    rules={{ required: false }}
                    render={({
                      field: { onChange, onBlur, value, ref },
                      fieldState: { invalid, isTouched, isDirty },
                    }) => (
                      <StyledFormControl
                        onChange={onChange}
                        value={value}
                        ref={ref}
                        onBlur={onBlur}
                        type="text"
                        isInvalid={isTouched && invalid}
                        isValid={isDirty && isTouched && !invalid}
                      />
                    )}
                  />
                  <Form.Text muted>Opcional</Form.Text>
                </Form.Group>

                <Form.Group as={Col} controlId="formUnidad" className="mb-3">
                  <Form.Label>Unidad</Form.Label>
                  <Controller
                    control={control}
                    name="unidad"
                    rules={{ required: true }}
                    render={({ field: { ref, onChange } }) => (
                      <>
                        <Form.Select
                          ref={ref}
                          onChange={onChange}
                          required={true}
                        >
                          <option key="blankChoice" hidden>
                            Seleccionar unidad...
                          </option>
                          {unidades?.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.nombre}
                            </option>
                          ))}
                        </Form.Select>
                      </>
                    )}
                  />
                  {errors.unidad && (
                    <Form.Control.Feedback type="invalid">
                      {errors.unidad.type === "required" && (
                        <span>Requerido</span>
                      )}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group
                  as={Col}
                  controlId="formPrecioUnitario"
                  className="mb-5"
                >
                  <Form.Label>Precio Unitario</Form.Label>
                  <Controller
                    control={control}
                    name="precioUnitario"
                    defaultValue={undefined}
                    rules={{ required: true, minLength: 1 }}
                    render={({
                      field: { onChange, onBlur, value, ref },
                      fieldState: { invalid, isTouched },
                    }) => (
                      <CurrencyFormat
                        customInput={Form.Control}
                        name="precioUnitario"
                        ref={ref}
                        value={value}
                        thousandSeparator={","}
                        onBlur={onBlur}
                        isInvalid={invalid}
                        required
                        onChange={onChange}
                        prefix={"$"}
                        decimalSeparator={"."}
                      />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    Campo debe ser un número mayor a 0.
                  </Form.Control.Feedback>
                </Form.Group>

                <ButtonsCol className="d-flex justify-content-center">
                  <SuccessButton type="submit" variant="success">
                    Finalizar
                  </SuccessButton>
                </ButtonsCol>
              </Form>
            </Row>
          </Card.Body>
        </Card>
      </div>
    </Row>
  );
}

const ButtonsCol = styled(Col)`
  gap: 1%;
`;

const StyledCardHeader = styled(Card.Header)`
  border-color: #eaeaea;
  position: relative;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 0px;
`;

const exitoOptions: ExitoOption[] = [
  {
    description: "Crear nuevo producto",
    path: "/productos/crear",
    variant: "primary",
  },
  {
    description: "Ver listado",
    path: "/productos",
    variant: "secondary",
    isLast: true,
  },
];

export default FormSection;
